import React, { useState, useEffect } from "react";
import zoomSdk from "@zoom/appssdk";
import Card from "@amzn/meridian/esm/components/card";
import Column from "@amzn/meridian/esm/components/column";
import Link from "@amzn/meridian/esm/components/link";
import RadioButton from "@amzn/meridian/esm/components/radio-button";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";

import FeedbackCard from "../../common/FeedbackCard";
import { FEEDBACK_PAGE } from "../../common/constants";
import LogManager from "../../utils/LogManager";
import { getPreference, updatePreference } from "../../utils/PreferenceUtil";
import Loading from "../../common/Loading";

import conciseIcon from "../../../images/verbosity_concise.svg";
import moderateIcon from "../../../images/verbosity_moderate.svg";
import detailedIcon from "../../../images/verbosity_detailed.svg";

const VERBOSITY_LEVELS = [
  { key: "concise", label: "Concise", icon: conciseIcon },
  { key: "moderate", label: "Moderate", icon: moderateIcon },
  { key: "detailed", label: "Detailed", icon: detailedIcon },
];

const LEARN_MORE_URL = "https://w.amazon.com/bin/view/AmazonMeetings/MeetingSummaries/AiMS/DetailedSummaries/";

/**
 * SettingsTabInClient Component
 *
 * Displays the settings tab content for the client interface, including
 * verbosity level selector and FeedbackCard in a wider layout.
 */
const SettingsTabInClient = () => {
  const [selectedLevel, setSelectedLevel] = useState(null);

  useEffect(() => {
    getPreference().then(setSelectedLevel);
  }, []);

  const handleLevelChange = async (key) => {
    setSelectedLevel(key);
    try {
      await updatePreference(key);
    } catch (error) {
      LogManager.error("Failed to update preference:", error);
    }
  };

  const handleLearnMoreClick = async () => {
    try {
      await zoomSdk.openUrl({ url: LEARN_MORE_URL });
    } catch (error) {
      LogManager.error("Failed to open Learn more URL:", error);
    }
  };

  if (!selectedLevel) return <Loading />;

  return (
    <Column spacing="none">
      <Row spacingInset="none" spacing="none">
        <Row width="74px" />
        <Row width="304px">
          <Column spacing="none" spacingInset="none" alignmentHorizontal="start">
            <Column height="72px" />
            <Text type="h100" fontFamily="amazonEmber">Summary detail level</Text>
            <Column height="12px" />
            <Row spacing="300" widths={["96px", "96px", "96px"]}>
              {VERBOSITY_LEVELS.map(({ key, label, icon }) => {
                const isSelected = selectedLevel === key;
                return (
                  <Theme key={key} tokens={{ boxBorderColorOutline: isSelected ? "#006D5B" : "#B1BAC3", cardBorderColor: isSelected ? "#006D5B" : "#B1BAC3", radioButtonDiameterSmall: 12, radioButtonIndicatorDiameterSmall: 6, radioButtonSpacingHorizontal: 8, radioButtonLabelSmallFontSize: 12, radioButtonLabelSmallLineHeight: "12px" }}>
                    <Card spacingInset="300" onClick={() => handleLevelChange(key)}>
                      <Column spacing="none" alignmentHorizontal="start">
                        <RadioButton checked={isSelected} name="verbosity" value={key} size="small" onChange={() => handleLevelChange(key)}>
                          <b>{label}</b>
                        </RadioButton>
                        <Column height="2px" />
                        <Row alignmentHorizontal="center" alignmentVertical="center" width="100%" height="36px">
                          <img src={icon} alt={label} />
                        </Row>
                      </Column>
                    </Card>
                  </Theme>
                );
              })}
            </Row>
            <Column height="8px" />
            <Link type="primary" onClick={handleLearnMoreClick}>Learn more</Link>
            <Column height="24px" />
          </Column>
        </Row>
        <Row width="24px" />
        <Row width="272px">
          <Column spacing="none" spacingInset="none" alignmentHorizontal="start">
            <Column height="72px" />
            <FeedbackCard page={FEEDBACK_PAGE.IN_CLIENT_FEEDBACK_TAB} />
          </Column>
        </Row>
      </Row>
    </Column>
  );
};

export default SettingsTabInClient;
